/**
 * Streaming-POST helper for Server-Sent-Events endpoints.
 *
 * Axios can't expose the raw Response body reader, so streaming endpoints
 * have to use `fetch` directly. This helper keeps them in sync with
 * `apiClient` by reading the auth token + active tenant from the same
 * localStorage keys.
 */
interface AuthStorage {
  state?: { accessToken?: string }
}

interface TenantStorage {
  state?: { activeTenantId?: string }
}

function readLocalStorage<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

function buildAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {}
  const auth = readLocalStorage<AuthStorage>('auth-storage')
  if (auth?.state?.accessToken) {
    headers.Authorization = `Bearer ${auth.state.accessToken}`
  }
  const tenant = readLocalStorage<TenantStorage>('tenant-storage')
  if (tenant?.state?.activeTenantId) {
    headers['X-Tenant-Id'] = tenant.state.activeTenantId
  }
  return headers
}

export interface StreamPostOptions {
  signal?: AbortSignal
  headers?: Record<string, string>
}

/**
 * POST `body` (serialised as JSON) to `path` with auth + tenant headers
 * injected from the same source as `apiClient`. Returns the raw Response
 * so the caller can consume the streaming body.
 *
 * `path` should be the same relative form `apiClient` uses (e.g.
 * `/ai/ticket-agent/123/message`) — the leading `/api` is added here.
 */
export async function streamPost(
  path: string,
  body: unknown,
  options: StreamPostOptions = {},
): Promise<Response> {
  const url = path.startsWith('/api') ? path : `/api${path.startsWith('/') ? '' : '/'}${path}`

  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...buildAuthHeaders(),
      ...(options.headers ?? {}),
    },
    body: JSON.stringify(body),
    ...(options.signal ? { signal: options.signal } : {}),
  })
}

export interface StreamEventLine {
  type: string
  [key: string]: unknown
}

/**
 * Read Server-Sent-Events (`data: {...}\n\n`) from a Response body and
 * yield each parsed event. Skips malformed lines silently.
 */
export async function* readSseStream(response: Response): AsyncGenerator<StreamEventLine> {
  if (!response.body) throw new Error('Keine Streaming-Antwort')

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() ?? ''

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue
      const jsonStr = line.slice(6).trim()
      if (!jsonStr) continue

      try {
        yield JSON.parse(jsonStr) as StreamEventLine
      } catch {
        /* skip malformed lines */
      }
    }
  }
}
